import {
    Container,
    Stack,
    Typography,
    Fab,
    Card,
    CardContent,
    Breadcrumbs,
    Link,
    ToggleButtonGroup,
    ToggleButton,
    Box,
    CircularProgress,
} from '@mui/material';
import { Add, Schedule, NewReleases } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useAuth, AuthStatus } from '../auth/Auth';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Thread } from '../database/forum';
import LoadingPage from '../loading/LoadingPage';
import { SubscriptionStatus } from '../database/user';
import ThreadCard from './ThreadCard';
import CreateThreadDialog from './CreateThreadDialog';
import { useRequest } from '../api/Request';
import { useApi } from '../api/Api';
import { CreateThreadRequest, ListThreadsResponse } from '../api/forumApi';
import LoadMoreButton from '../bts/components/LoadMoreButton';
import { useHasForumAccess } from './useHasForumAccess';
import LockedContentOverlay from './LockedContentOverlay';
import { CommunityThemeProvider } from '../ThemeProvider';

export enum ThreadSortOrder {
    RECENT = 'recent',
    OLDEST = 'oldest',
}

const TopicDetailPage = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const api = useApi();
    const listThreadsRequest = useRequest();
    const [data, setData] = useState<ListThreadsResponse>();
    const [loading, setLoading] = useState(true);
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [replyToThread, setReplyToThread] = useState<Thread | undefined>();
    const [sortOrder, setSortOrder] = useState<ThreadSortOrder>(ThreadSortOrder.OLDEST);
    const [newThreadSk, setNewThreadSk] = useState<string>();
    const threadRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
    const hasForumAccess = useHasForumAccess();
    const [searchParams, setSearchParams] = useSearchParams();
    const linkedThreadSk = searchParams.get('thread') || undefined;
    const [highlightedThreadSk, setHighlightedThreadSk] = useState<string>();
    const [linkedThreadData, setLinkedThreadData] = useState<Thread>();
    const linkedThreadFetched = useRef(false);
    const { forumNameAndSk, topicNameAndSk } = useParams<{
        forumNameAndSk: string;
        topicNameAndSk: string;
    }>();

    // Parse forum name and SK from URL
    // React Router automatically decodes the URL parameter
    const forumSeparator = '-FORUM#';
    const splitForumIndex = forumNameAndSk?.lastIndexOf(forumSeparator) ?? -1;
    const forumName =
        splitForumIndex > 0 ? forumNameAndSk?.substring(0, splitForumIndex) : undefined;
    const forumSk =
        splitForumIndex > 0 ? forumNameAndSk?.substring(splitForumIndex + 1) : undefined; // Skip just the dash, keep "FORUM#..."

    // Parse topic name and SK from URL
    // React Router automatically decodes the URL parameter
    const topicSeparator = '-TOPIC#';
    const splitTopicIndex = topicNameAndSk?.lastIndexOf(topicSeparator) ?? -1;
    const topicName =
        splitTopicIndex > 0 ? topicNameAndSk?.substring(0, splitTopicIndex) : undefined;
    const topicSk =
        splitTopicIndex > 0 ? topicNameAndSk?.substring(splitTopicIndex + 1) : undefined; // Skip just the dash, keep "TOPIC#..."

    const handleResponse = useCallback((resp: ListThreadsResponse) => {
        setData({
            threads: resp.threads,
            lastEvaluatedKey: resp.lastEvaluatedKey,
        });
        setLoading(false);
    }, []);

    const handleResponseLoadMore = useCallback(
        (resp: ListThreadsResponse) => {
            const newEntries = (data?.threads || []).concat(resp.threads);

            setData({
                threads: newEntries,
                lastEvaluatedKey: resp.lastEvaluatedKey,
            });
        },
        [data],
    );

    const onDelete = (i: number) => {
        const newData = data?.threads || [];
        setData({
            threads: [...newData.slice(0, i), ...newData.slice(i + 1)],
            lastEvaluatedKey: data?.lastEvaluatedKey || '',
        });
    };

    useEffect(() => {
        if (!forumSk || !topicSk || !forumName || !topicName) {
            navigate('/ess');
            return;
        }

        setLoading(true);
        listThreadsRequest.onStart();
        api.listThreads(topicSk, sortOrder)
            .then((resp) => {
                listThreadsRequest.onSuccess();
                handleResponse(resp.data);
            })
            .catch((error) => {
                listThreadsRequest.onFailure(error);
                console.error('Failed to load threads:', error);
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        forumSk,
        topicSk,
        forumName,
        topicName,
        sortOrder,
        api,
        navigate,
        handleResponse,
    ]);

    // Scroll to newly created thread
    useEffect(() => {
        if (newThreadSk) {
            const threadElement = threadRefs.current.get(newThreadSk);
            if (threadElement) {
                threadElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setNewThreadSk(undefined);
            }
        }
    }, [newThreadSk, data]);

    // Deep-link: fetch linked thread if not in loaded list
    useEffect(() => {
        if (!linkedThreadSk || !data || linkedThreadFetched.current) return;

        const found = data.threads.find((t) => t.sk === linkedThreadSk);
        if (found) {
            setHighlightedThreadSk(linkedThreadSk);
            setTimeout(() => setHighlightedThreadSk(undefined), 5000);
            setTimeout(() => {
                const el = threadRefs.current.get(linkedThreadSk);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
            return;
        }

        // Not in loaded list — fetch it
        linkedThreadFetched.current = true;
        api.getThread(topicSk!, linkedThreadSk)
            .then((resp) => {
                setLinkedThreadData(resp.data);
                setHighlightedThreadSk(linkedThreadSk);
                setTimeout(() => setHighlightedThreadSk(undefined), 5000);
            })
            .catch((err) => {
                console.error('Failed to fetch linked thread:', err);
            });
    }, [linkedThreadSk, data, api]);

    // Scroll to fetched linked thread once it renders
    useEffect(() => {
        if (linkedThreadData && highlightedThreadSk) {
            setTimeout(() => {
                const el = threadRefs.current.get(highlightedThreadSk);
                if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 100);
        }
    }, [linkedThreadData, highlightedThreadSk]);

    const clearLinkedThread = () => {
        setHighlightedThreadSk(undefined);
        setLinkedThreadData(undefined);
        linkedThreadFetched.current = false;
        setSearchParams((prev) => {
            prev.delete('thread');
            return prev;
        });
    };

    // Early return after all hooks
    if (!forumSk || !topicSk || !forumName || !topicName) {
        return <LoadingPage />;
    }

    // Check if user can create threads (authenticated and subscribed)
    const canCreateThread =
        auth.status === AuthStatus.Authenticated &&
        auth.user !== undefined &&
        (auth.user.subscriptionStatus === SubscriptionStatus.SUBSCRIBED ||
            auth.user.subscriptionOverride ||
            auth.user.isArtist);

    const handleCreateThread = async (req: CreateThreadRequest, file?: File) => {
        try {
            const resp = await api.createThread(forumSk, topicSk, req);

            // If there's a file, wait for upload to complete before updating UI
            if (file) {
                try {
                    // Generate presigned URL
                    const presignedResp = await api.putPresignedUrl(
                        forumSk,
                        topicSk,
                        resp.data.sk,
                        file.type,
                    );

                    // Upload file and wait for completion
                    await api.uploadMediaToS3({
                        presignedUrl: presignedResp.data.presignedUrl,
                        contentType: file.type,
                        mediaContent: file,
                    });
                } catch (error) {
                    console.error('Failed to upload media:', error);
                    // Still add thread to UI even if media upload fails
                }
            }

            // Add thread to UI after media upload completes (or if no media)
            setData((prevData) => {
                const existingThreads = prevData?.threads || [];
                const updatedThreads =
                    sortOrder === ThreadSortOrder.RECENT
                        ? [resp.data, ...existingThreads]
                        : [...existingThreads, resp.data];
                return {
                    threads: updatedThreads,
                    lastEvaluatedKey: prevData?.lastEvaluatedKey,
                };
            });

            // Set the new thread SK for scrolling
            setNewThreadSk(resp.data.sk);
        } catch (error) {
            console.error('Failed to create thread:', error);
            throw error; // Re-throw so the dialog knows it failed
        }
    };

    const onSave = (i: number, thread: Thread) => {
        const newData = data?.threads || [];
        setData({
            threads: [...newData.slice(0, i), thread, ...newData.slice(i + 1)],
            lastEvaluatedKey: data?.lastEvaluatedKey || '',
        });
    };

    const handleReply = (thread: Thread) => {
        setReplyToThread(thread);
        setCreateDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setCreateDialogOpen(false);
        setReplyToThread(undefined);
    };

    const onLoadMore = () => {
        if (!topicSk) return;

        listThreadsRequest.onStart();
        api.listThreads(topicSk, sortOrder, data?.lastEvaluatedKey || '')
            .then((resp) => {
                handleResponseLoadMore(resp.data);
                listThreadsRequest.onSuccess(resp.data);
            })
            .catch((err) => {
                console.error('listThreads: ', err);
                listThreadsRequest.onFailure(err);
            });
    };

    if (auth.status === AuthStatus.Loading || loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <CommunityThemeProvider>
        <Box
            sx={(theme) => ({
                minHeight: '100vh',
                bgcolor: 'background.default',
                background: `radial-gradient(ellipse at 50% 0%, ${theme.palette.primary.main}08 0%, #060f1e 45%, #030810 100%)`,
            })}
        >
        <Container maxWidth='md' sx={{ pt: 4, pb: 12 }}>
            <Stack spacing={3}>
                {/* Breadcrumb Navigation */}
                <Breadcrumbs aria-label='breadcrumb' color='secondary'>
                    <Link
                        component='button'
                        variant='h6'
                        underline='always'
                        fontFamily='LemonMilk'
                        color='secondary'
                        onClick={() => navigate('/ess')}
                        sx={{
                            cursor: 'pointer',
                        }}
                    >
                        Forums
                    </Link>
                    <Link
                        component='button'
                        variant='h6'
                        underline='always'
                        fontFamily='LemonMilk'
                        color='secondary'
                        onClick={() => {
                            // Re-encode the forum name and SK for the URL
                            const encodedPath = `${encodeURIComponent(
                                forumName!,
                            )}-${encodeURIComponent(forumSk!)}`;
                            navigate(`/ess/${encodedPath}`);
                        }}
                        sx={{
                            cursor: 'pointer',
                        }}
                    >
                        {forumName}
                    </Link>
                    <Typography variant='h6' fontFamily='LemonMilk' color='secondary'>
                        {topicName}
                    </Typography>
                </Breadcrumbs>

                {/* Sort Toggle */}
                {data && data.threads.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <ToggleButtonGroup
                            value={sortOrder}
                            exclusive
                            onChange={(_, newSort) => {
                                if (newSort !== null) {
                                    setSortOrder(newSort);
                                }
                            }}
                            aria-label='sort order'
                            size='small'
                        >
                            <ToggleButton
                                value={ThreadSortOrder.RECENT}
                                aria-label='most recent'
                            >
                                <NewReleases sx={{ mr: 1 }} />
                                Recent
                            </ToggleButton>
                            <ToggleButton
                                value={ThreadSortOrder.OLDEST}
                                aria-label='oldest first'
                            >
                                <Schedule sx={{ mr: 1 }} />
                                Oldest
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}

                {/* Threads list */}
                {!data || data.threads.length === 0 ? (
                    <Card>
                        <CardContent>
                            <Typography variant='body1' textAlign='center'>
                                No threads yet. Be the first to contribute!
                            </Typography>
                        </CardContent>
                    </Card>
                ) : (
                    <LockedContentOverlay
                        locked={!hasForumAccess}
                        message='Subscribe to unlock threads and join the conversation'
                        blurAmount={5}
                    >
                        <Stack spacing={2}>
                            {/* Show separately-fetched linked thread at top */}
                            {linkedThreadData &&
                                !data.threads.find(
                                    (t) => t.sk === linkedThreadData.sk,
                                ) && (
                                    <Box
                                        key={linkedThreadData.sk}
                                        ref={(el: HTMLDivElement | null) => {
                                            if (el) {
                                                threadRefs.current.set(
                                                    linkedThreadData.sk,
                                                    el,
                                                );
                                            } else {
                                                threadRefs.current.delete(
                                                    linkedThreadData.sk,
                                                );
                                            }
                                        }}
                                    >
                                        <ThreadCard
                                            forumSk={forumSk}
                                            thread={linkedThreadData}
                                            onSave={(thread) =>
                                                setLinkedThreadData(thread)
                                            }
                                            onReply={handleReply}
                                            canReply={canCreateThread}
                                            onDelete={clearLinkedThread}
                                            locked={!hasForumAccess}
                                            highlighted={true}
                                        />
                                    </Box>
                                )}
                            {data.threads.map((thread, i) => (
                                <Box
                                    key={thread.sk}
                                    ref={(el: HTMLDivElement | null) => {
                                        if (el) {
                                            threadRefs.current.set(thread.sk, el);
                                        } else {
                                            threadRefs.current.delete(thread.sk);
                                        }
                                    }}
                                >
                                    <ThreadCard
                                        forumSk={forumSk}
                                        thread={thread}
                                        onSave={(thread) => onSave(i, thread)}
                                        onReply={handleReply}
                                        canReply={canCreateThread}
                                        onDelete={() => onDelete(i)}
                                        locked={!hasForumAccess}
                                        highlighted={highlightedThreadSk === thread.sk}
                                    />
                                </Box>
                            ))}
                        </Stack>
                    </LockedContentOverlay>
                )}
                <LoadMoreButton
                    request={listThreadsRequest}
                    startKey={data?.lastEvaluatedKey || ''}
                    onLoadMore={onLoadMore}
                />
            </Stack>

            {/* Floating Action Button for creating threads */}
            {canCreateThread && (
                <Fab
                    color='primary'
                    sx={{
                        position: 'fixed',
                        bottom: 80,
                        right: 16,
                    }}
                    onClick={() => {
                        setReplyToThread(undefined);
                        setCreateDialogOpen(true);
                    }}
                >
                    <Add />
                </Fab>
            )}

            {/* Create Thread Dialog */}
            <CreateThreadDialog
                open={createDialogOpen}
                onClose={handleCloseDialog}
                onSubmit={handleCreateThread}
                replyToThread={replyToThread}
                forumSk={forumSk}
                topicSk={topicSk}
            />
        </Container>
        </Box>
        </CommunityThemeProvider>
    );
};

export default TopicDetailPage;
